import type { Framework } from '@toeverything/infra';

import {
  PERFORMANCE_METRICS_CHANNEL,
  type WorkerPerformanceMessage,
} from './channels';
import { installAPIInterceptor } from './services/api-interceptor';
import { PerformanceMonitorService } from './services/performance-monitor';
import { monitorIndexedDB, monitorSharedWorker } from './utils/worker-monitor';

export function setupPerformanceMonitoring(framework: Framework) {
  const perfService = framework.provider().get(PerformanceMonitorService);
  if (!perfService) return;

  perfService.mark('app:init:start');

  let workerMetricsChannel: BroadcastChannel | null = null;

  const cleanupWorkerChannel = () => {
    if (workerMetricsChannel) {
      workerMetricsChannel.close();
      workerMetricsChannel = null;
    }
  };

  const onWorkerMetricsMessage = (
    event: MessageEvent<WorkerPerformanceMessage>
  ) => {
    const data = event.data;
    if (!data) return;

    if (data.kind === 'worker-fetch') {
      perfService.recordAPITiming({
        url: data.url,
        method: data.method,
        startTime: data.startTime,
        duration: data.duration,
        status: data.status,
        type: 'worker-fetch',
        size: data.size,
        source: 'worker',
      });
    } else if (data.kind === 'worker-websocket') {
      const method =
        data.event === 'open'
          ? 'CONNECT'
          : data.event === 'first-message'
            ? 'FIRST_MESSAGE'
            : 'CLOSE';
      perfService.recordAPITiming({
        url: data.url,
        method,
        startTime: data.startTime,
        duration: data.duration,
        type: 'worker-websocket',
        source: 'worker',
      });

      if (data.event === 'first-message') {
        perfService.setWebSocketMetrics({
          connectionStart: data.startTime,
          connectionEstablished: data.duration,
          firstDataReceived: data.duration,
        });
      }
    }
  };

  if (typeof BroadcastChannel !== 'undefined') {
    workerMetricsChannel = new BroadcastChannel(PERFORMANCE_METRICS_CHANNEL);
    workerMetricsChannel.addEventListener('message', onWorkerMetricsMessage);
  }

  installAPIInterceptor(perfService);
  monitorIndexedDB(perfService);
  perfService.collectWebVitals().catch(() => {});

  window.addEventListener('beforeunload', cleanupWorkerChannel);
}

export function setupWorkerMonitoring(
  worker: SharedWorker | Worker,
  framework: Framework
) {
  try {
    const perfService = framework.provider().get(PerformanceMonitorService);
    return monitorSharedWorker(worker, perfService);
  } catch (e) {
    return worker;
  }
}
